import React, { useState, useEffect } from 'react';

function validateIPv4(ip) {
    const parts = ip.split('.');
    if (parts.length !== 4) return false;
    return parts.every(p => { const n = parseInt(p, 10); return !isNaN(n) && n >= 0 && n <= 255 && String(n) === p; });
}

function validateIPv6(ip) {
    return /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/.test(ip) || /^::$/.test(ip) || /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/.test(ip);
}

function validateHostname(h) {
    return /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}\.?$/.test(h) || h === '@';
}

function validateRecord(formData) {
    const errors = {};
    const content = (formData.content || '').trim();

    if (!content) {
        errors.content = 'Conteudo e obrigatorio.';
        return errors;
    }

    switch (formData.type) {
        case 'A':
            if (!validateIPv4(content)) errors.content = 'IPv4 invalido. Ex: 192.168.1.1';
            break;
        case 'AAAA':
            if (!validateIPv6(content)) errors.content = 'IPv6 invalido. Ex: 2001:db8::1';
            break;
        case 'CNAME':
            if (validateIPv4(content) || validateIPv6(content)) {
                errors.content = 'CNAME nao pode apontar para IP. Use um registro A/AAAA.';
            } else if (!validateHostname(content) && content !== '@') {
                errors.content = 'Hostname invalido.';
            }
            break;
        case 'MX':
            if (!validateHostname(content)) errors.content = 'Hostname invalido para MX.';
            if (formData.priority < 0 || formData.priority > 65535) errors.priority = 'Prioridade deve ser entre 0 e 65535.';
            break;
        case 'TXT':
            if (content.length > 2048) errors.content = `TXT muito longo (${content.length}/2048 chars).`;
            break;
        default:
            break;
    }
    return errors;
}

const RecordModal = ({ record, zoneName, onSave, onClose }) => {
    const [formData, setFormData] = useState({
        type: 'A',
        name: zoneName,
        content: '',
        ttl: 3600,
        proxied: false,
        priority: 10
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (record) {
            setFormData({
                type: record.type,
                name: record.name,
                content: record.content,
                ttl: record.ttl,
                proxied: record.proxied || false,
                priority: record.priority || 10
            });
        }
    }, [record]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const errs = validateRecord(formData);
        setErrors(errs);
        if (Object.keys(errs).length === 0) {
            onSave(formData);
        }
    };

    const handleChange = (field, value) => {
        const updated = { ...formData, [field]: value };
        setFormData(updated);
        if (Object.keys(errors).length > 0) {
            setErrors(validateRecord(updated));
        }
    };

    const recordTypes = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA'];
    const hasErrors = Object.keys(errors).length > 0;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2 className="modal-title">
                        {record ? '\u270F\uFE0F Editar Registro' : '\u2795 Novo Registro'}
                    </h2>
                    <button className="close-btn" onClick={onClose}>{'\u00D7'}</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label">Tipo</label>
                        <select
                            className="form-select"
                            value={formData.type}
                            onChange={(e) => handleChange('type', e.target.value)}
                            required
                        >
                            {recordTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Nome</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder={`exemplo.${zoneName}`}
                            required
                        />
                        <small className="form-hint">
                            Use @ para o dominio raiz ou o nome completo (ex: www.{zoneName})
                        </small>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Conteudo</label>
                        {formData.type === 'TXT' ? (
                            <textarea
                                className={`form-input ${errors.content ? 'form-input-error' : ''}`}
                                rows="3"
                                value={formData.content}
                                onChange={(e) => handleChange('content', e.target.value)}
                                placeholder="Conteudo do registro TXT"
                                required
                            />
                        ) : (
                            <input
                                type="text"
                                className={`form-input ${errors.content ? 'form-input-error' : ''}`}
                                value={formData.content}
                                onChange={(e) => handleChange('content', e.target.value)}
                                placeholder={
                                    formData.type === 'A' ? '192.0.2.1' :
                                    formData.type === 'AAAA' ? '2001:0db8::1' :
                                    formData.type === 'CNAME' ? `destino.${zoneName}` :
                                    formData.type === 'MX' ? `mail.${zoneName}` :
                                    'Conteudo do registro'
                                }
                                required
                            />
                        )}
                        {errors.content && <span className="field-error">{errors.content}</span>}
                        {formData.type === 'TXT' && (
                            <small className="form-hint">{formData.content.length}/2048 caracteres</small>
                        )}
                    </div>

                    {formData.type === 'MX' && (
                        <div className="form-group">
                            <label className="form-label">Prioridade</label>
                            <input
                                type="number"
                                className={`form-input ${errors.priority ? 'form-input-error' : ''}`}
                                value={formData.priority}
                                onChange={(e) => handleChange('priority', parseInt(e.target.value))}
                                min="0"
                                max="65535"
                                required
                            />
                            {errors.priority && <span className="field-error">{errors.priority}</span>}
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">TTL (segundos)</label>
                        <select
                            className="form-select"
                            value={formData.ttl}
                            onChange={(e) => handleChange('ttl', parseInt(e.target.value))}
                        >
                            <option value="1">Auto</option>
                            <option value="60">1 minuto</option>
                            <option value="300">5 minutos</option>
                            <option value="600">10 minutos</option>
                            <option value="1800">30 minutos</option>
                            <option value="3600">1 hora</option>
                            <option value="7200">2 horas</option>
                            <option value="18000">5 horas</option>
                            <option value="43200">12 horas</option>
                            <option value="86400">1 dia</option>
                        </select>
                    </div>

                    {(formData.type === 'A' || formData.type === 'AAAA' || formData.type === 'CNAME') && (
                        <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    className="form-checkbox"
                                    checked={formData.proxied}
                                    onChange={(e) => handleChange('proxied', e.target.checked)}
                                />
                                <span className="form-label" style={{ marginBottom: 0 }}>
                                    Proxied (usar CDN da Cloudflare)
                                </span>
                            </label>
                            <small className="form-hint">
                                Quando ativado, o trafego passa pela Cloudflare (CDN, protecao DDoS)
                            </small>
                        </div>
                    )}

                    <div className="action-buttons">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={hasErrors}>
                            {record ? 'Salvar Alteracoes' : 'Criar Registro'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RecordModal;
